import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../shared/providers/LanguageProvider';
import { ShimmerTable, ShimmerBox } from '../../components/common/Shimmer';
import offerService from '../../services/offerService';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const MerchantOffers = () => {
  const { userProfile, session } = useAuth();
  const { t } = useLanguage(); // eslint-disable-line no-unused-vars
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchMerchantOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.token, session?.access_token]);

  const fetchMerchantOffers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = userProfile?.token || session?.access_token;
      if (!token) {
        setError('No authentication token available');
        return;
      }

      const data = await offerService.getMerchantOffers(token);
      setOffers(data || []);
    } catch (error) {
      console.error('Error fetching merchant offers:', error);
      setError(error.response?.data?.message || error.message || 'Failed to fetch offers');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ar-SA');
  };

  const getStatusBadge = (status, validFrom, validUntil) => {
    const now = new Date();
    const fromDate = validFrom ? new Date(validFrom) : null;
    const untilDate = validUntil ? new Date(validUntil) : null;

    if (status === 'paused') {
      return <Badge variant="warning">معلقة</Badge>;
    }
    
    if (status === 'expired') {
      return <Badge variant="destructive">منتهية الصلاحية</Badge>;
    }

    if (untilDate && untilDate < now) {
      return <Badge variant="destructive">منتهية الصلاحية</Badge>;
    }

    if (fromDate && fromDate > now) {
      return <Badge variant="secondary">قادمة</Badge>;
    }

    return <Badge variant="success">نشطة</Badge>;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <ShimmerBox width="w-48" height="h-8" />
          <ShimmerBox width="w-32" height="h-10" />
        </div>
        <Card>
          <CardContent className="p-0">
            <ShimmerTable rows={5} columns={6} />
          </CardContent>
        </Card>
      </div>
    );
  }

  const filteredOffers = offers.filter(offer => 
    offer.offer_title?.toLowerCase().includes(search.toLowerCase()) ||
    offer.offer_description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="العروض الخاصة بي"
        description="عرض جميع العروض المخصصة لمنتجاتك (للقراءة فقط)"
        actions={<Button onClick={fetchMerchantOffers}>تحديث</Button>}
      />

      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6 text-destructive">{error}</CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          <Input
            type="text"
            placeholder="البحث في العروض..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-md"
          />
        </CardContent>
      </Card>

      <Card>
        {offers.length === 0 ? (
          <CardContent className="py-8 text-center">
            <div className="mb-4 text-6xl text-muted-foreground">🎫</div>
            <h3 className="mb-2 text-lg font-medium text-foreground">لا توجد عروض متاحة</h3>
            <p className="text-muted-foreground">
              لم يتم إنشاء أي عروض لمنتجاتك بعد. سيتم إنشاء العروض من قبل الإدارة بناءً على اتفاقيات مع العملاء.
            </p>
          </CardContent>
        ) : (
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">عنوان العرض</TableHead>
                  <TableHead className="text-right">الوصف</TableHead>
                  <TableHead className="text-right">نسبة الخصم</TableHead>
                  <TableHead className="text-right">صالح من</TableHead>
                  <TableHead className="text-right">صالح حتى</TableHead>
                  <TableHead className="text-right">الحالة</TableHead>
                  <TableHead className="text-right">الحد الأقصى للاستخدام</TableHead>
                  <TableHead className="text-right">الحد الأدنى للرصيد</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOffers.map((offer) => (
                  <TableRow key={offer.id}>
                    <TableCell className="font-medium">{offer.offer_title || '-'}</TableCell>
                    <TableCell className="max-w-xs truncate">{offer.offer_description || '-'}</TableCell>
                    <TableCell>{offer.discount_percentage ? `${offer.discount_percentage}%` : '-'}</TableCell>
                    <TableCell>{formatDate(offer.valid_from)}</TableCell>
                    <TableCell>{formatDate(offer.valid_until)}</TableCell>
                    <TableCell>{getStatusBadge(offer.status, offer.valid_from, offer.valid_until)}</TableCell>
                    <TableCell>{offer.max_uses ? offer.max_uses : 'غير محدود'}</TableCell>
                    <TableCell>{offer.min_wallet_balance ? `SAR ${offer.min_wallet_balance}` : 'غير مطلوب'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        )}
      </Card>

      {offers.length > 0 && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle className="text-sm">ℹ️ معلومات مهمة</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              هذه العروض للقراءة فقط. يتم إنشاء العروض من قبل الإدارة بناءً على اتفاقيات مع العملاء. 
              يمكنك إضافة المنتجات في قسم &quot;المنتجات&quot; وستظهر العروض هنا بمجرد إنشائها.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MerchantOffers;
